import { AmlLocation } from '@mark43/cad-api';
import type { AmlCallView } from '../../phone-calls/types';
import { ESRI_DEFAULT_ICON_LABEL_FONT } from '../configurations';
import RapidSOSInactiveIcon from '../images/phone-icons/unselected/RapidSOS_unselected.svg';
import RapidSOSActiveIcon from '../images/phone-icons/selected/RapidSOS_selected.svg';
import type {
    AmlLocationGeoPointEntity,
    IndexedAmlLocationEntity,
    LabelClassProperties,
} from '../types';
import { isValidLatLng } from '../../location/helpers';
import { getLastAmlCallLocation } from '../../phone-calls/phone-calls-store';
import { colors } from '../../colors/colors';

// AML (RapidSOS)

const convertAmlLocationToAmlLocationGeoPointEntity = (
    amlLocation?: AmlLocation
): AmlLocationGeoPointEntity | undefined => {
    if (amlLocation && isValidLatLng(amlLocation.latitude, amlLocation.longitude)) {
        return {
            entityId: amlLocation.id,
            amlPhoneCallId: amlLocation.amlPhoneCallId,
            latitude: amlLocation.latitude,
            longitude: amlLocation.longitude,
            uncertaintyRadius: amlLocation.uncertaintyRadius,
            timestamp: amlLocation.locationTimestamp,
        };
    }
    return undefined;
};

export const getAmlCallLastAmlLocationGeoPointEntity = (
    amlCall?: AmlCallView
): AmlLocationGeoPointEntity | undefined => {
    if (amlCall?.locations?.length) {
        const lastAmlLocation = getLastAmlCallLocation(amlCall.locations);

        return convertAmlLocationToAmlLocationGeoPointEntity(lastAmlLocation);
    }
    return undefined;
};

export const getDrawLocationsFromPhoneCall = (
    amlCall: AmlCallView | undefined
): IndexedAmlLocationEntity[] => {
    if (!amlCall) {
        return [];
    }

    // only interested in (up to) the previous 7 locations in reverse-chronological order
    return (amlCall.locations ?? [])
        .slice(-8, -1)
        .reverse()
        .map((location, index) => {
            return {
                ...location,
                entityId: location.id,
                index,
            };
        });
};

export const amlIconLabel: LabelClassProperties = {
    symbol: {
        type: 'text',
        color: colors.mapLabelText,
        font: ESRI_DEFAULT_ICON_LABEL_FONT,
        haloSize: '1px',
        haloColor: colors.mapLabelHalo,
    },
    labelPlacement: 'below-center',
    labelExpressionInfo: {
        expression: "'RapidSOS'",
    },
    maxScale: 0,
    minScale: 25000000,
};

export const amlCallUniqueValueInfos = [
    {
        value: 'rapidsos_inactive',
        symbol: {
            type: 'picture-marker',
            url: RapidSOSInactiveIcon,
            width: '32px',
            height: '32px',
        },
    },
    {
        value: 'rapidsos_selected',
        symbol: {
            type: 'picture-marker',
            url: RapidSOSActiveIcon,
            width: '32px',
            height: '32px',
        },
    },
];

export const amlIconRenderer = {
    type: 'unique-value', // autocasts as new UniqueValueRenderer()
    field: 'iconName',
    uniqueValueInfos: amlCallUniqueValueInfos,
    authoringInfo: undefined,
    defaultSymbol: {
        type: 'picture-marker',
        url: RapidSOSInactiveIcon,
        width: '32px',
        height: '32px',
    },
};

export const amlLocationHistoryIconRenderer = {
    type: 'unique-value', // autocasts as new UniqueValueRenderer()
    field: 'iconName',
    uniqueValueInfos: [
        {
            value: 'rapidsos_previous',
            symbol: {
                color: colors.esriColors.amlLocationPin,
                outline: {
                    width: 0,
                },
                size: 20,
                style: 'circle',
                type: 'simple-marker',
            },
        },
    ],
    visualVariables: [
        {
            type: 'opacity',
            field: 'index',
            stops: [
                { value: 0, opacity: 0.5 },
                { value: 1, opacity: 0.5 },
                { value: 2, opacity: 0.5 },
                { value: 3, opacity: 0.4 },
                { value: 4, opacity: 0.3 },
                { value: 5, opacity: 0.2 },
                { value: 6, opacity: 0.1 },
            ],
        },
    ],
    authoringInfo: undefined,
};
